import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { fields, generateTimeSlots } from '../../data/mockData';
import { cancelBookingWithResult, createBooking, getAllBookings, submitBookingPaymentProof } from '../../data/bookingStore';
import { getCustomerById, getOwnerProfileById, getSession, logoutUser } from '../../data/authStore';
import {
  createBookingStatusNotifications,
  getNotificationDeliveryPreferences,
  getNotificationPreferences,
  getNotificationsForCustomer,
  getReminderPlansForCustomer,
  getWaitlistEntriesForCustomer,
  markAllNotificationsRead,
  processDueReminders,
  setNotificationPreference,
  setNotificationDeliveryPreference,
} from '../../data/engagementStore';
import { NotificationPreferenceKey } from '../../types';
import { isSlotBlockedBySubscription } from '../../data/subscriptionStore';
import { dispatchBookingStatusMessages } from '../../services/messagingApi';

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const session = getSession();
  const [refreshKey, setRefreshKey] = useState(0);
  void refreshKey;

  const allBookings = getAllBookings();

  useEffect(() => {
    processDueReminders();
    setRefreshKey((v) => v + 1);
  }, []);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const months = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
    return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  };

  const formatHourValue = (value: number) => {
    const totalMinutes = Math.round(value * 60);
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  const getBookingStatusLabel = (status: string) => {
    if (status === 'pending_owner_approval' || status === 'pending') return 'SS Onayı Bekliyor';
    if (status === 'awaiting_payment') return 'Ödeme Bekleniyor';
    if (status === 'payment_review') return 'Dekont İncelemede';
    if (status === 'confirmed') return 'Onaylandı';
    if (status === 'cancelled') return 'İptal Edildi';
    return status;
  };

  const [actionInfo, setActionInfo] = useState('');
  const [rescheduleBookingId, setRescheduleBookingId] = useState('');
  const [rescheduleDate, setRescheduleDate] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [rescheduleHour, setRescheduleHour] = useState<number | null>(null);
  const [statusNote, setStatusNote] = useState('');
  const [paymentProofInputs, setPaymentProofInputs] = useState<Record<string, { url: string; note: string }>>({});
  const notificationLabels: Record<NotificationPreferenceKey, string> = {
    booking: 'Rezervasyon bildirimleri',
    waitlist: 'Bekleme listesi bildirimleri',
    reminder: 'Hatırlatma bildirimleri',
    subscription: 'Abonelik bildirimleri',
  };
  const notificationKeys: NotificationPreferenceKey[] = ['booking', 'waitlist', 'reminder', 'subscription'];

  if (!session) {
    return (
      <div className="bg-slate-50 min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-xl border border-gray-200 p-6 text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Profil için giriş gerekli</h2>
          <p className="text-sm text-gray-600 mb-4">Profilinizi görmek için hesabınıza giriş yapın.</p>
          <Link to="/giris" className="inline-flex items-center rounded-lg bg-primary-600 text-white px-4 py-2 text-sm font-medium">
            Giriş Yap
          </Link>
        </div>
      </div>
    );
  }

  if (session.role === 'admin') {
    return (
      <div className="bg-slate-50 min-h-screen">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h1 className="text-2xl font-bold text-gray-900">ADMİN Profili</h1>
            <p className="text-sm text-gray-600 mt-2">Sistem yönetimi için admin panelini kullanabilirsiniz.</p>
            <div className="mt-5 flex items-center gap-3">
              <Link to="/admin/panel" className="rounded-lg bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 text-sm">
                Admin Paneline Git
              </Link>
              <button
                type="button"
                onClick={() => {
                  logoutUser();
                  navigate('/');
                }}
                className="rounded-lg border border-gray-300 bg-white text-gray-700 px-4 py-2 text-sm"
              >
                Çıkış Yap
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (session.role === 'owner') {
    const owner = getOwnerProfileById(session.ownerId);
    const ownerNotifications = getNotificationsForCustomer(session.ownerId, owner?.phone);
    const ownerUnreadCount = ownerNotifications.filter((item) => !item.read).length;
    const ownerNotificationPrefs = getNotificationPreferences({ userId: session.ownerId, contactPhone: owner?.phone });
    const ownerDeliveryPrefs = getNotificationDeliveryPreferences({ userId: session.ownerId, contactPhone: owner?.phone });
    const ownerFields = fields.filter((field) => field.ownerId === session.ownerId);
    const ownerBookings = allBookings
      .filter((booking) => ownerFields.some((field) => field.id === booking.fieldId))
      .sort((a, b) => b.date.localeCompare(a.date) || b.hour - a.hour);

    return (
      <div className="bg-slate-50 min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{owner?.fullName || 'Saha Sahibi'}</h1>
              <p className="text-sm text-gray-600 mt-1">@{owner?.username || '-'} • {owner?.phone || '-'}</p>
              <p className="text-sm text-gray-600 mt-1">
                Durum: {owner?.suspended ? 'Askıda' : owner?.status === 'approved' ? 'Onaylı' : owner?.status === 'pending' ? 'Onay Bekliyor' : 'Reddedildi'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Link to="/panel" className="rounded-lg bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 text-sm">
                Saha Paneli
              </Link>
              <button
                type="button"
                onClick={() => {
                  logoutUser();
                  navigate('/');
                }}
                className="rounded-lg border border-gray-300 bg-white text-gray-700 px-4 py-2 text-sm"
              >
                Çıkış Yap
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between gap-3 mb-3">
              <h2 className="text-lg font-semibold text-gray-900">
                Bildirimler {ownerUnreadCount > 0 ? `(${ownerUnreadCount} yeni)` : ''}
              </h2>
              {ownerNotifications.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    markAllNotificationsRead(session.ownerId, owner?.phone);
                    setRefreshKey((v) => v + 1);
                  }}
                  className="text-xs rounded-lg border border-gray-300 bg-white text-gray-700 px-3 py-1.5"
                >
                  Tümünü Okundu Yap
                </button>
              )}
            </div>
            {ownerNotifications.length === 0 ? (
              <p className="text-sm text-gray-600 mb-4">Henüz bildiriminiz yok.</p>
            ) : (
              <div className="space-y-2 mb-4">
                {ownerNotifications.slice(0, 8).map((item) => (
                  <div
                    key={item.id}
                    className={`rounded-lg border p-3 ${item.read ? 'border-gray-200 bg-gray-50' : 'border-primary-200 bg-primary-50'}`}
                  >
                    <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                    <p className="text-xs text-gray-600 mt-1">{item.message}</p>
                  </div>
                ))}
              </div>
            )}
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Bildirim Ayarları</h3>
            <div className="grid sm:grid-cols-2 gap-2">
              {notificationKeys.map((key) => {
                const enabled = ownerNotificationPrefs[key];
                return (
                  <button
                    key={`owner-pref-${key}`}
                    type="button"
                    onClick={() => {
                      setNotificationPreference({
                        userId: session.ownerId,
                        contactPhone: owner?.phone,
                        key,
                        enabled: !enabled,
                      });
                      setRefreshKey((v) => v + 1);
                    }}
                    className={`text-xs rounded-lg border px-3 py-2 text-left ${enabled ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-gray-300 bg-white text-gray-600'}`}
                  >
                    {notificationLabels[key]}: {enabled ? 'Açık' : 'Kapalı'}
                  </button>
                );
              })}
            </div>
            <div className="grid sm:grid-cols-2 gap-2 mt-3">
              <button
                type="button"
                onClick={() => {
                  setNotificationDeliveryPreference({
                    userId: session.ownerId,
                    contactPhone: owner?.phone,
                    key: 'browser',
                    enabled: !ownerDeliveryPrefs.browser,
                  });
                  setRefreshKey((v) => v + 1);
                }}
                className={`text-xs rounded-lg border px-3 py-2 text-left ${ownerDeliveryPrefs.browser ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-gray-300 bg-white text-gray-600'}`}
              >
                Tarayıcı Bildirimi: {ownerDeliveryPrefs.browser ? 'Açık' : 'Kapalı'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setNotificationDeliveryPreference({
                    userId: session.ownerId,
                    contactPhone: owner?.phone,
                    key: 'sound',
                    enabled: !ownerDeliveryPrefs.sound,
                  });
                  setRefreshKey((v) => v + 1);
                }}
                className={`text-xs rounded-lg border px-3 py-2 text-left ${ownerDeliveryPrefs.sound ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-gray-300 bg-white text-gray-600'}`}
              >
                Sesli Uyarı: {ownerDeliveryPrefs.sound ? 'Açık' : 'Kapalı'}
              </button>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Sahalarım ({ownerFields.length})</h2>
            {ownerFields.length === 0 ? (
              <p className="text-sm text-gray-600">Henüz saha eklenmemiş.</p>
            ) : (
              <div className="space-y-3">
                {ownerFields.map((field) => (
                  <div key={field.id} className="rounded-lg border border-gray-200 p-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{field.name}</p>
                      <p className="text-xs text-gray-600">{field.district} • {(field.sizeOptions || [field.size]).join(', ')}</p>
                    </div>
                    <Link to={`/saha/${field.id}`} className="text-sm text-primary-600 hover:underline">Profili Gör</Link>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Rezervasyonlar ({ownerBookings.length})</h2>
            {ownerBookings.length === 0 ? (
              <p className="text-sm text-gray-600">Rezervasyon bulunamadı.</p>
            ) : (
              <div className="space-y-3">
                {ownerBookings.slice(0, 30).map((booking) => (
                  <div key={booking.id} className="rounded-lg border border-gray-200 p-3">
                    <p className="text-sm font-semibold text-gray-900">{booking.fieldName} • {booking.teamName}</p>
                    <p className="text-xs text-gray-600 mt-1">
                      {formatDate(booking.date)} • {formatHourValue(booking.hour)} - {formatHourValue(booking.hour + booking.duration)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Durum: {booking.status}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const customer = getCustomerById(session.userId);
  const notifications = getNotificationsForCustomer(session.userId, customer?.phone);
  const unreadCount = notifications.filter((item) => !item.read).length;
  const customerNotificationPrefs = getNotificationPreferences({ userId: session.userId, contactPhone: customer?.phone });
  const customerDeliveryPrefs = getNotificationDeliveryPreferences({ userId: session.userId, contactPhone: customer?.phone });
  const waitlistEntries = getWaitlistEntriesForCustomer(session.userId, customer?.phone);
  const reminderPlans = getReminderPlansForCustomer(session.userId, customer?.phone)
    .filter((item) => !item.sentAt)
    .slice(0, 8);
  const customerBookings = allBookings
    .filter((booking) => booking.customerId === session.userId)
    .sort((a, b) => b.date.localeCompare(a.date) || b.hour - a.hour);
  const today = new Date().toISOString().split('T')[0];
  const pastBookings = customerBookings.filter((booking) => booking.date < today || booking.status === 'cancelled');
  const nextBookings = customerBookings.filter((booking) => booking.date >= today && booking.status !== 'cancelled');

  const rescheduleTarget = nextBookings.find((booking) => booking.id === rescheduleBookingId);
  const rescheduleSlots = rescheduleTarget
    ? generateTimeSlots(rescheduleTarget.fieldId, rescheduleDate).filter((slot) => {
        if (slot.isBooked) return false;
        const blocked = isSlotBlockedBySubscription(
          rescheduleTarget.fieldId,
          rescheduleDate,
          slot.label || `${formatHourValue(slot.hour)}-${formatHourValue(slot.hour + 1)}`,
          undefined
        );
        return !blocked;
      })
    : [];

  const handleCancelByCustomer = async (bookingId: string) => {
    const result = cancelBookingWithResult(bookingId);
    if (!result.ok || !result.booking) {
      setActionInfo('İptal işlemi başarısız.');
      return;
    }
    const booking = result.booking;
    const field = fields.find((f) => f.id === booking.fieldId);
    const owner = field ? getOwnerProfileById(field.ownerId) : undefined;
    createBookingStatusNotifications({
      status: 'cancelled',
      referenceCode: booking.id.toUpperCase(),
      fieldId: booking.fieldId,
      fieldName: booking.fieldName,
      date: booking.date,
      startTime: formatHourValue(booking.hour),
      endTime: formatHourValue(booking.hour + booking.duration),
      customerId: booking.customerId,
      customerPhone: booking.contactPhone,
      ownerId: field?.ownerId,
      ownerPhone: owner?.phone,
    });
    try {
      await dispatchBookingStatusMessages({
        status: 'cancelled',
        referenceCode: booking.id.toUpperCase(),
        fieldName: booking.fieldName,
        date: booking.date,
        startTime: formatHourValue(booking.hour),
        endTime: formatHourValue(booking.hour + booking.duration),
        customerName: booking.contactName,
        customerPhone: booking.contactPhone,
        ownerName: owner?.fullName,
        ownerPhone: owner?.phone,
        byRole: 'customer',
        note: statusNote.trim() || undefined,
      });
      setActionInfo('Rezervasyon iptal edildi. İki tarafa bilgilendirme mesajı gönderildi.');
    } catch {
      setActionInfo('Rezervasyon iptal edildi. Mesaj servisine ulaşılamadı.');
    }
    setRefreshKey((v) => v + 1);
  };

  const handleRescheduleByCustomer = async () => {
    if (!rescheduleTarget || rescheduleHour === null) return;
    const newBookingResult = createBooking({
      fieldId: rescheduleTarget.fieldId,
      fieldName: rescheduleTarget.fieldName,
      date: rescheduleDate,
      hour: rescheduleHour,
      duration: rescheduleTarget.duration,
      teamName: rescheduleTarget.teamName,
      contactName: rescheduleTarget.contactName,
      contactPhone: rescheduleTarget.contactPhone,
      totalPrice: rescheduleTarget.totalPrice,
      customerId: rescheduleTarget.customerId,
    });
    if (!newBookingResult.ok) {
      setActionInfo(newBookingResult.error);
      return;
    }

    const cancelOld = cancelBookingWithResult(rescheduleTarget.id);
    if (!cancelOld.ok) {
      setActionInfo('Yeni rezervasyon oluşturuldu fakat eski rezervasyon iptal edilemedi.');
      return;
    }

    const field = fields.find((f) => f.id === rescheduleTarget.fieldId);
    const owner = field ? getOwnerProfileById(field.ownerId) : undefined;
    createBookingStatusNotifications({
      status: 'rescheduled',
      referenceCode: rescheduleTarget.id.toUpperCase(),
      fieldId: rescheduleTarget.fieldId,
      fieldName: rescheduleTarget.fieldName,
      date: rescheduleTarget.date,
      startTime: formatHourValue(rescheduleTarget.hour),
      endTime: formatHourValue(rescheduleTarget.hour + rescheduleTarget.duration),
      customerId: rescheduleTarget.customerId,
      customerPhone: rescheduleTarget.contactPhone,
      ownerId: field?.ownerId,
      ownerPhone: owner?.phone,
    });
    try {
      await dispatchBookingStatusMessages({
        status: 'rescheduled',
        referenceCode: rescheduleTarget.id.toUpperCase(),
        fieldName: rescheduleTarget.fieldName,
        date: rescheduleTarget.date,
        startTime: formatHourValue(rescheduleTarget.hour),
        endTime: formatHourValue(rescheduleTarget.hour + rescheduleTarget.duration),
        customerName: rescheduleTarget.contactName,
        customerPhone: rescheduleTarget.contactPhone,
        ownerName: owner?.fullName,
        ownerPhone: owner?.phone,
        byRole: 'customer',
        note: statusNote.trim() || undefined,
        newDate: rescheduleDate,
        newStartTime: formatHourValue(rescheduleHour),
        newEndTime: formatHourValue(rescheduleHour + rescheduleTarget.duration),
      });
      setActionInfo('Rezervasyon saati değiştirildi ve bilgilendirme mesajı gönderildi.');
    } catch {
      setActionInfo('Saat değiştirildi ancak mesaj servisine ulaşılamadı.');
    }
    setRescheduleBookingId('');
    setRescheduleHour(null);
    setRefreshKey((v) => v + 1);
  };

  const handleSubmitPaymentProof = (bookingId: string) => {
    const input = paymentProofInputs[bookingId] || { url: '', note: '' };
    const result = submitBookingPaymentProof(bookingId, {
      customerId: session.userId,
      proofUrl: input.url,
      proofNote: input.note,
    });
    if (!result.ok) {
      setActionInfo(result.error || 'Dekont gönderilemedi. Lütfen tekrar deneyin.');
      return;
    }
    setPaymentProofInputs((prev) => ({ ...prev, [bookingId]: { url: '', note: '' } }));
    setActionInfo('Dekont gönderildi. Saha sahibinin ödeme onayı bekleniyor.');
    setRefreshKey((v) => v + 1);
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
        <div className="bg-white rounded-xl border border-gray-200 p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{customer?.fullName || 'Kullanıcı Profili'}</h1>
            <p className="text-sm text-gray-600 mt-1">@{customer?.username || '-'} • {customer?.phone || '-'}</p>
          </div>
          <button
            type="button"
            onClick={() => {
              logoutUser();
              navigate('/');
            }}
            className="rounded-lg border border-gray-300 bg-white text-gray-700 px-4 py-2 text-sm"
          >
            Çıkış Yap
          </button>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between gap-3 mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Bildirimler {unreadCount > 0 ? `(${unreadCount} yeni)` : ''}
            </h2>
            {notifications.length > 0 && (
              <button
                type="button"
                onClick={() => {
                  markAllNotificationsRead(session.userId, customer?.phone);
                  setRefreshKey((v) => v + 1);
                }}
                className="text-xs rounded-lg border border-gray-300 bg-white text-gray-700 px-3 py-1.5"
              >
                Tümünü Okundu Yap
              </button>
            )}
          </div>

          {notifications.length === 0 ? (
            <p className="text-sm text-gray-600">Henüz bildiriminiz yok.</p>
          ) : (
            <div className="space-y-3">
              {notifications.slice(0, 10).map((item) => (
                <div
                  key={item.id}
                  className={`rounded-lg border p-3 ${item.read ? 'border-gray-200 bg-gray-50' : 'border-primary-200 bg-primary-50'}`}
                >
                  <p className="text-sm font-semibold text-gray-900">{item.title}</p>
                  <p className="text-xs text-gray-600 mt-1">{item.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Bildirim Ayarları</h2>
          <div className="grid sm:grid-cols-2 gap-2">
            {notificationKeys.map((key) => {
              const enabled = customerNotificationPrefs[key];
              return (
                <button
                  key={`customer-pref-${key}`}
                  type="button"
                  onClick={() => {
                    setNotificationPreference({
                      userId: session.userId,
                      contactPhone: customer?.phone,
                      key,
                      enabled: !enabled,
                    });
                    setRefreshKey((v) => v + 1);
                  }}
                  className={`text-xs rounded-lg border px-3 py-2 text-left ${enabled ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-gray-300 bg-white text-gray-600'}`}
                >
                  {notificationLabels[key]}: {enabled ? 'Açık' : 'Kapalı'}
                </button>
              );
            })}
          </div>
          <div className="grid sm:grid-cols-2 gap-2 mt-3">
            <button
              type="button"
              onClick={() => {
                setNotificationDeliveryPreference({
                  userId: session.userId,
                  contactPhone: customer?.phone,
                  key: 'browser',
                  enabled: !customerDeliveryPrefs.browser,
                });
                setRefreshKey((v) => v + 1);
              }}
              className={`text-xs rounded-lg border px-3 py-2 text-left ${customerDeliveryPrefs.browser ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-gray-300 bg-white text-gray-600'}`}
            >
              Tarayıcı Bildirimi: {customerDeliveryPrefs.browser ? 'Açık' : 'Kapalı'}
            </button>
            <button
              type="button"
              onClick={() => {
                setNotificationDeliveryPreference({
                  userId: session.userId,
                  contactPhone: customer?.phone,
                  key: 'sound',
                  enabled: !customerDeliveryPrefs.sound,
                });
                setRefreshKey((v) => v + 1);
              }}
              className={`text-xs rounded-lg border px-3 py-2 text-left ${customerDeliveryPrefs.sound ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-gray-300 bg-white text-gray-600'}`}
            >
              Sesli Uyarı: {customerDeliveryPrefs.sound ? 'Açık' : 'Kapalı'}
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Planlanan Hatırlatmalar</h2>
            {reminderPlans.length === 0 ? (
              <p className="text-sm text-gray-600">Planlanmış hatırlatma yok.</p>
            ) : (
              <div className="space-y-3">
                {reminderPlans.map((item) => (
                  <div key={item.id} className="rounded-lg border border-gray-200 p-3">
                    <p className="text-sm font-semibold text-gray-900">{item.fieldName}</p>
                    <p className="text-xs text-gray-600 mt-1">
                      Tetikleme: {new Date(item.triggerAt).toLocaleString('tr-TR')}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Maç: {item.date} • {formatHourValue(item.hour)} - {formatHourValue(item.hour + 1)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Bekleme Listem</h2>
            {waitlistEntries.length === 0 ? (
              <p className="text-sm text-gray-600">Bekleme listesi kaydınız yok.</p>
            ) : (
              <div className="space-y-3">
                {waitlistEntries.slice(0, 10).map((item) => (
                  <div key={item.id} className="rounded-lg border border-gray-200 p-3">
                    <p className="text-sm font-semibold text-gray-900">{item.fieldName}</p>
                    <p className="text-xs text-gray-600 mt-1">{item.date} • {item.label}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Durum: {item.status === 'active' ? 'Sırada' : item.status === 'notified' ? 'Yer Açıldı' : item.status === 'converted' ? 'Rezervasyona Dönüştü' : 'İptal'}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Yaklaşan Rezervasyonlar ({nextBookings.length})</h2>
            <div className="mb-3">
              <label className="block text-xs text-gray-600 mb-1">İptal / Değişim Notu (opsiyonel)</label>
              <input
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
                className="w-full bg-gray-50 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="Karşı tarafa gönderilecek not"
              />
              {actionInfo && <p className="text-xs text-indigo-700 mt-2">{actionInfo}</p>}
            </div>
            {nextBookings.length === 0 ? (
              <p className="text-sm text-gray-600">Yaklaşan rezervasyon yok.</p>
            ) : (
              <div className="space-y-3">
                {nextBookings.map((booking) => (
                  <div key={booking.id} className="rounded-lg border border-gray-200 p-3">
                    <p className="text-sm font-semibold text-gray-900">{booking.fieldName}</p>
                    <p className="text-xs text-gray-600 mt-1">{formatDate(booking.date)} • {formatHourValue(booking.hour)} - {formatHourValue(booking.hour + booking.duration)}</p>
                    <p className="text-xs text-gray-500 mt-1">Takım: {booking.teamName}</p>
                    <p className="text-xs text-gray-500 mt-1">Durum: {getBookingStatusLabel(booking.status)}</p>
                    {booking.status === 'payment_review' && (
                      <p className="text-xs text-indigo-700 mt-1">Dekont gönderildi, saha sahibinin onayı bekleniyor.</p>
                    )}
                    {booking.status === 'awaiting_payment' && booking.paymentProofRejectedReason && (
                      <p className="text-xs text-rose-700 mt-1">
                        Son dekont notu: {booking.paymentProofRejectedReason}
                      </p>
                    )}
                    <div className="mt-3 flex items-center gap-2">
                      {booking.status === 'awaiting_payment' && (
                        <>
                          <input
                            type="url"
                            value={paymentProofInputs[booking.id]?.url || ''}
                            onChange={(e) =>
                              setPaymentProofInputs((prev) => ({
                                ...prev,
                                [booking.id]: { url: e.target.value, note: prev[booking.id]?.note || '' },
                              }))
                            }
                            placeholder="Dekont linki (drive, banka ekran görüntüsü vb.)"
                            className="text-xs w-full max-w-sm rounded-lg border border-gray-300 bg-white px-2.5 py-1.5"
                          />
                          <input
                            type="text"
                            value={paymentProofInputs[booking.id]?.note || ''}
                            onChange={(e) =>
                              setPaymentProofInputs((prev) => ({
                                ...prev,
                                [booking.id]: { url: prev[booking.id]?.url || '', note: e.target.value },
                              }))
                            }
                            placeholder="Dekont notu (opsiyonel)"
                            className="text-xs w-full max-w-xs rounded-lg border border-gray-300 bg-white px-2.5 py-1.5"
                          />
                          <button
                            type="button"
                            onClick={() => handleSubmitPaymentProof(booking.id)}
                            className="text-xs rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 px-2.5 py-1"
                          >
                            Dekont Gönder
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        onClick={() => handleCancelByCustomer(booking.id)}
                        className="text-xs rounded-lg border border-rose-200 bg-rose-50 text-rose-700 px-2.5 py-1"
                      >
                        İptal Et
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setRescheduleBookingId(booking.id);
                          setRescheduleDate(booking.date);
                          setRescheduleHour(null);
                        }}
                        className="text-xs rounded-lg border border-blue-200 bg-blue-50 text-blue-700 px-2.5 py-1"
                      >
                        Saati Değiştir
                      </button>
                    </div>
                    {rescheduleBookingId === booking.id && (
                      <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-2">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <input
                            type="date"
                            value={rescheduleDate}
                            onChange={(e) => setRescheduleDate(e.target.value)}
                            className="bg-white border border-gray-300 rounded px-2 py-1.5 text-xs"
                          />
                          <select
                            value={rescheduleHour ?? ''}
                            onChange={(e) => setRescheduleHour(Number(e.target.value))}
                            className="bg-white border border-gray-300 rounded px-2 py-1.5 text-xs"
                          >
                            <option value="">Yeni saat seçin</option>
                            {rescheduleSlots.map((slot) => (
                              <option key={slot.hour} value={slot.hour}>
                                {slot.label || `${formatHourValue(slot.hour)}-${formatHourValue(slot.hour + 1)}`}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={handleRescheduleByCustomer}
                            disabled={rescheduleHour === null}
                            className="text-xs rounded-lg bg-primary-600 text-white px-2.5 py-1 disabled:opacity-50"
                          >
                            Yeni Saate Geç
                          </button>
                          <button
                            type="button"
                            onClick={() => setRescheduleBookingId('')}
                            className="text-xs rounded-lg border border-gray-300 bg-white text-gray-700 px-2.5 py-1"
                          >
                            Vazgeç
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Geçmiş Rezervasyonlar ({pastBookings.length})</h2>
            {pastBookings.length === 0 ? (
              <p className="text-sm text-gray-600">Geçmiş rezervasyon yok.</p>
            ) : (
              <div className="space-y-3">
                {pastBookings.map((booking) => (
                  <div key={booking.id} className="rounded-lg border border-gray-200 p-3">
                    <p className="text-sm font-semibold text-gray-900">{booking.fieldName}</p>
                    <p className="text-xs text-gray-600 mt-1">{formatDate(booking.date)} • {formatHourValue(booking.hour)} - {formatHourValue(booking.hour + booking.duration)}</p>
                    <p className="text-xs text-gray-500 mt-1">Durum: {getBookingStatusLabel(booking.status)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
